import {
  Link,
  Navigate,
  Outlet,
  RouteObject,
  useLocation,
  useParams,
} from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import t from "@/lang/lang";
import { GfsCatalogList } from "@/services/backend/gfs/catalog/components/catalog-list";
import {
  GfsCatalogConsumables,
  GfsCatalogDetail,
} from "@/services/backend/gfs/catalog/components/catalog-detail";
import { GfsCatalogMonitorEditor } from "@/services/backend/gfs/catalog/components/catalog-monitor-editor";
import { useGfsCatalogGetQuery } from "@/services/backend/gfs/catalog/api";
import { formatMonitorType } from "@/services/backend/pestcontrol/lib/monitor";
import { GfsOfferConfigPage } from "@/services/backend/gfs/offer/components/offer-config-page";
import { GfsOfferParametersPage } from "@/services/backend/gfs/offer/components/offer-parameters-page";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  TabsNav,
  TabsNavLink,
  TabsNavList,
} from "@/shared/components/layout/tabs-nav";

export const gfsBaseDataRoutes: RouteObject[] = [
  { index: true, element: <Navigate to="catalog" replace /> },
  {
    path: "offer",
    element: <GfsOfferBaseDataRoutePage />,
  },
  {
    path: "parameters",
    element: <GfsOfferParametersBaseDataRoutePage />,
  },
  {
    path: "catalog",
    element: <GfsCatalogRoute />,
    children: [
      { index: true, element: <GfsCatalogRoutePage /> },
      { path: ":catalogId", element: <GfsCatalogDetailRoutePage /> },
      {
        path: ":catalogId/consumables",
        element: <GfsCatalogConsumablesRoutePage />,
      },
      { path: ":catalogId/:itemId", element: <GfsCatalogMonitorRoutePage /> },
    ],
  },
];

function GfsOfferBaseDataRoutePage() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Angebotstexte")}</CardTitle>
        <CardDescription>
          {t(
            "Textbausteine für neue GfS-Angebote. Änderungen werden automatisch gespeichert.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <GfsOfferConfigPage />
      </CardContent>
    </Card>
  );
}

function GfsOfferParametersBaseDataRoutePage() {
  return <GfsOfferParametersPage />;
}

function GfsCatalogRoute() {
  const { catalogId, itemId } = useParams();
  const { pathname } = useLocation();
  const basePath = "/base-data/processes/gfs";
  const isConsumablesRoute = pathname.endsWith("/consumables");
  const { data: catalog } = useGfsCatalogGetQuery(
    { id: catalogId! },
    { skip: !catalogId },
  );
  const item = catalog?.items.find((candidate) => candidate.id === itemId);

  return (
    <Card>
      <CardContent>
        <Breadcrumb>
          <BreadcrumbList>
            {!catalogId && (
              <BreadcrumbItem>
                <BreadcrumbPage>{t("Katalogübersicht")}</BreadcrumbPage>
              </BreadcrumbItem>
            )}
            {catalogId && catalog && (
              <>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={`${basePath}/catalog`}>
                      {t("Katalogübersicht")}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  {item || isConsumablesRoute ? (
                    <BreadcrumbLink asChild>
                      <Link to={`${basePath}/catalog/${catalogId}`}>
                        {catalog.name || t("Katalog")}
                      </Link>
                    </BreadcrumbLink>
                  ) : (
                    <BreadcrumbPage>
                      {catalog.name || t("Katalog")}
                    </BreadcrumbPage>
                  )}
                </BreadcrumbItem>
                {item && (
                  <>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbLink asChild>
                        <Link
                          to={`${basePath}/catalog/${catalogId}?type=${item.monitor.type}`}
                        >
                          {formatMonitorType(item.monitor.type)}
                        </Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbPage>
                        {item.monitor.name || t("Monitor")}
                      </BreadcrumbPage>
                    </BreadcrumbItem>
                  </>
                )}
                {isConsumablesRoute && (
                  <>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbPage>
                        {t("Verbrauchsmaterialien")}
                      </BreadcrumbPage>
                    </BreadcrumbItem>
                  </>
                )}
              </>
            )}
          </BreadcrumbList>
        </Breadcrumb>
      </CardContent>
      <Outlet />
    </Card>
  );
}

function GfsCatalogRoutePage() {
  return (
    <>
      <CardHeader>
        <CardTitle>{t("Monitorkataloge")}</CardTitle>
        <CardDescription>
          {t("Hier können Kataloge angelegt und bearbeitet werden.")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <GfsCatalogList />
      </CardContent>
    </>
  );
}

function GfsCatalogDetailRoutePage() {
  const { catalogId } = useParams();
  if (!catalogId) return null;

  return (
    <>
      <CardHeader>
        <CardTitle>{t("GfS-Katalog")}</CardTitle>
        <CardDescription>
          {t("Hier können Monitore im Katalog bearbeitet werden.")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <GfsCatalogSubNavigation catalogId={catalogId} />
        <div className="pt-4">
          <GfsCatalogDetail catalogId={catalogId} />
        </div>
      </CardContent>
    </>
  );
}

function GfsCatalogConsumablesRoutePage() {
  const { catalogId } = useParams();
  const { data, error, isLoading } = useGfsCatalogGetQuery(
    { id: catalogId! },
    { skip: !catalogId },
  );

  if (!catalogId) return null;

  return (
    <>
      <CardHeader>
        <CardTitle>{t("Verbrauchsmaterialien")}</CardTitle>
        <CardDescription>
          {t(
            "Hier können Verbrauchsmaterialien für den Katalog verwaltet werden.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <GfsCatalogSubNavigation catalogId={catalogId} />
        <div className="pt-4">
          {error ? (
            <RTKQueryErrorAlert error={error} />
          ) : isLoading || !data ? (
            <div className="text-muted-foreground p-4 text-sm">
              {t("Lade Katalog ...")}
            </div>
          ) : (
            <GfsCatalogConsumables
              catalogId={data.id}
              consumables={data.consumables ?? []}
            />
          )}
        </div>
      </CardContent>
    </>
  );
}

function GfsCatalogSubNavigation({ catalogId }: { catalogId: string }) {
  const basePath = `/base-data/processes/gfs/catalog/${catalogId}`;

  return (
    <TabsNav>
      <TabsNavList>
        <TabsNavLink to={basePath} end>
          {t("Monitore")}
        </TabsNavLink>
        <TabsNavLink to={`${basePath}/consumables`}>
          {t("Verbrauchsmaterialien")}
        </TabsNavLink>
      </TabsNavList>
    </TabsNav>
  );
}

function GfsCatalogMonitorRoutePage() {
  const { catalogId, itemId } = useParams();
  if (!catalogId || !itemId) return null;

  return (
    <>
      <CardHeader>
        <CardTitle>{t("Monitor bearbeiten")}</CardTitle>
        <CardDescription>
          {t("Hier können Monitore im Katalog bearbeitet werden.")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <GfsCatalogMonitorEditor catalogId={catalogId} itemId={itemId} />
      </CardContent>
    </>
  );
}
